/**
 * Loads the closure calendar and hands it to `primeClosures`.
 *
 * Mounted once per app shell. Everything else keeps reading the synchronous accessors in
 * `config/blackoutDates`, which answer from the compiled-in fallback until this resolves and
 * from the database afterwards — so there is no loading state for callers to handle and no
 * window in which the calendar reads as empty.
 *
 * Waits for a session. `program_closures` is readable `TO authenticated` only, so an anon
 * read from the login page is a 401, and one that failed there stays failed after an in-page
 * password sign-in — nothing would ask again. The listener is supabase's own rather than
 * `useAuth`, because this sits above `AuthProvider` to serve the admin and staff shells too.
 *
 * Deliberately does not surface an error. A failed load leaves the fallback in place, which
 * is last-deploy's calendar: stale at worst, never open. Blocking the app on it would trade a
 * silent, safe degradation for a white screen.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { fetchClosures } from '../services/closures';
import { primeClosures, type BlackoutDate } from '../config/blackoutDates';

/** Long: the calendar changes a few times a year, and the fallback covers a cold start. */
const STALE_MS = 60 * 60 * 1000;

export function useClosures() {
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    // Fires INITIAL_SESSION on subscribe, so a page load with a stored session starts at once.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSignedIn(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const query = useQuery<BlackoutDate[]>({
    queryKey: ['program-closures'],
    queryFn: fetchClosures,
    enabled: signedIn,
    staleTime: STALE_MS,
    // A closure that failed to load is not worth hammering; the fallback is serving.
    retry: 1,
  });

  useEffect(() => {
    if (!query.data) return;
    // `primeClosures` refuses an empty set — see its doc comment. An RLS misconfiguration
    // returns [] with HTTP 200, which is indistinguishable from a truly empty table, and
    // treating it as "nothing is closed" is the one failure that could bill a closed day.
    if (!primeClosures(query.data) && query.data.length === 0) {
      console.warn('[closures] ignored an empty program_closures response; keeping fallback calendar');
    }
  }, [query.data]);

  return query;
}
